import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Users, FileQuestion, GraduationCap, CreditCard, Sparkles, Trash2, Plus, Gift, Bell, Upload, Check, X, Clock, BarChart3, Image as ImageIcon, Eye, Globe, Megaphone, Pencil, Crown } from "lucide-react";
import QuestionText from "@/components/QuestionText";

const SUBJECTS = ["Matematik", "Türkçe", "Fen Bilimleri", "Sosyal Bilgiler", "İngilizce"];
const BE = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [exams, setExams] = useState([]);
  const [topics, setTopics] = useState([]);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [payments, setPayments] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/questions").then(({ data }) => setQuestions(data));
    api.get("/exams").then(({ data }) => setExams(data));
    api.get("/topics").then(({ data }) => setTopics(data));
    api.get("/admin/users").then(({ data }) => setMembers(data));
    api.get("/admin/trial-requests").then(({ data }) => setRequests(data));
    api.get("/admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get("/admin/payments").then(({ data }) => setPayments(data)).catch(() => {});
    api.get("/admin/announcements").then(({ data }) => setAnnouncements(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  const pending = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">🛡️ Yönetim Paneli</h1>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <AStat icon={Users} color="bg-blue-500" value={stats.total_students} label="Öğrenci" />
          <AStat icon={FileQuestion} color="bg-rose-500" value={stats.total_questions} label="Soru" />
          <AStat icon={GraduationCap} color="bg-emerald-500" value={stats.total_exams} label="Deneme" />
          <AStat icon={CreditCard} color="bg-amber-500" value={stats.paid_subscriptions} label="Abonelik" />
          <AStat icon={Sparkles} color="bg-purple-500" value={stats.total_attempts} label="Çözülen" />
          <AStat icon={Bell} color="bg-orange-500" value={stats.pending_trials} label="Bekleyen Talep" />
        </div>
      )}

      <Tabs defaultValue="topics">
        <TabsList className="bg-slate-100 rounded-2xl flex-wrap h-auto">
          <TabsTrigger value="members" data-testid="tab-members">Üyeler</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Ödemeler</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">İstatistikler</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="relative">
            Deneme Talepleri
            {pending > 0 && <span data-testid="pending-badge" className="ml-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{pending}</span>}
          </TabsTrigger>
          <TabsTrigger value="topics" data-testid="tab-topics">Ders İçerikleri</TabsTrigger>
          <TabsTrigger value="announcements" data-testid="tab-announcements">Duyurular</TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions">Soru Bankası</TabsTrigger>
          <TabsTrigger value="generate" data-testid="tab-generate">AI Soru Üret</TabsTrigger>
          <TabsTrigger value="exams" data-testid="tab-exams">Denemeler</TabsTrigger>
        </TabsList>

        <TabsContent value="members"><MembersTab members={members} reload={load} /></TabsContent>
        <TabsContent value="payments"><PaymentsTab payments={payments} revenue={stats?.revenue_tl || 0} /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab analytics={analytics} /></TabsContent>
        <TabsContent value="requests"><RequestsTab requests={requests} reload={load} /></TabsContent>
        <TabsContent value="topics"><TopicsTab topics={topics} reload={load} /></TabsContent>
        <TabsContent value="announcements"><AnnouncementsTab announcements={announcements} reload={load} /></TabsContent>
        <TabsContent value="questions"><QuestionsTab questions={questions} reload={load} /></TabsContent>
        <TabsContent value="generate"><GenerateTab reload={load} /></TabsContent>
        <TabsContent value="exams"><ExamsTab exams={exams} questions={questions} reload={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

function AStat({ icon: Icon, color, value, label }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
      <div className={`${color} w-9 h-9 rounded-xl flex items-center justify-center text-white`}><Icon className="w-5 h-5" /></div>
      <p className="mt-2 text-xl font-extrabold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function subLabel(m) {
  if (m.subscription_type === "trial") return { text: "Deneme", cls: "bg-emerald-100 text-emerald-700" };
  if (m.subscription_type === "paid") return { text: "Premium", cls: "bg-amber-100 text-amber-700" };
  if (m.is_subscribed) return { text: "Aktif", cls: "bg-emerald-100 text-emerald-700" };
  return { text: "Ücretsiz", cls: "bg-slate-100 text-slate-500" };
}

function MembersTab({ members, reload }) {
  const [days, setDays] = useState({});
  const grant = async (uid) => {
    await api.post(`/admin/users/${uid}/grant-trial`, { days: Number(days[uid]) || 7 });
    toast.success("Deneme süresi verildi 🎁");
    reload();
  };
  const grantSub = async (uid) => {
    await api.post(`/admin/users/${uid}/grant-subscription`, { days: 30 });
    toast.success("30 günlük abonelik verildi 👑");
    reload();
  };
  const revoke = async (uid) => {
    await api.post(`/admin/users/${uid}/revoke`);
    toast.success("Erişim kaldırıldı");
    reload();
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 mt-4">
      <h3 className="font-bold text-slate-900 mb-3">Üyeler ({members.length})</h3>
      <div className="space-y-2 max-h-[560px] overflow-y-auto">
        {members.map((m) => {
          const lbl = subLabel(m);
          return (
            <div key={m.user_id} data-testid={`member-${m.user_id}`} className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl">
              <div className="flex-1 min-w-[160px]">
                <p className="text-sm font-semibold text-slate-800">{m.name}</p>
                <p className="text-xs text-slate-500">{m.email}</p>
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${lbl.cls}`}>{lbl.text}</span>
              {m.subscription_until && (
                <span className="text-xs text-slate-400">{new Date(m.subscription_until).toLocaleDateString("tr-TR")}</span>
              )}
              <Input type="number" value={days[m.user_id] ?? 7} onChange={(e) => setDays({ ...days, [m.user_id]: e.target.value })}
                data-testid={`days-${m.user_id}`} className="w-16 h-9 rounded-lg text-center" />
              <Button data-testid={`grant-${m.user_id}`} onClick={() => grant(m.user_id)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg h-9">
                <Gift className="w-4 h-4 mr-1" /> Deneme Ver
              </Button>
              <Button data-testid={`grant-sub-${m.user_id}`} onClick={() => grantSub(m.user_id)} size="sm" className="bg-amber-500 hover:bg-amber-600 rounded-lg h-9">
                <Crown className="w-4 h-4 mr-1" /> 30 Gün Abonelik
              </Button>
              {m.is_subscribed && (
                <Button data-testid={`revoke-${m.user_id}`} onClick={() => revoke(m.user_id)} size="sm" variant="outline" className="rounded-lg h-9 text-rose-600 border-rose-200">
                  Kaldır
                </Button>
              )}
            </div>
          );
        })}
        {members.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Henüz üye yok.</p>}
      </div>
    </div>
  );
}

function RequestsTab({ requests, reload }) {
  const [days, setDays] = useState({});
  const approve = async (id) => {
    await api.post(`/admin/trial-requests/${id}/approve`, { days: Number(days[id]) || 7 });
    toast.success("Talep onaylandı, deneme başladı 🎉");
    reload();
  };
  const reject = async (id) => {
    await api.post(`/admin/trial-requests/${id}/reject`);
    toast.success("Talep reddedildi");
    reload();
  };
  const badge = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-600" };
  const txt = { pending: "Bekliyor", approved: "Onaylandı", rejected: "Reddedildi" };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 mt-4">
      <h3 className="font-bold text-slate-900 mb-3">Ücretsiz Deneme Talepleri</h3>
      <div className="space-y-2 max-h-[560px] overflow-y-auto">
        {requests.map((r) => (
          <div key={r.id} data-testid={`request-${r.id}`} className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl">
            <Clock className="w-4 h-4 text-slate-400" />
            <div className="flex-1 min-w-[160px]">
              <p className="text-sm font-semibold text-slate-800">{r.name}</p>
              <p className="text-xs text-slate-500">{r.email} · {new Date(r.created_at).toLocaleString("tr-TR")}</p>
            </div>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${badge[r.status]}`}>{txt[r.status]}</span>
            {r.status === "pending" && (
              <>
                <Input type="number" value={days[r.id] ?? 7} onChange={(e) => setDays({ ...days, [r.id]: e.target.value })}
                  data-testid={`req-days-${r.id}`} className="w-16 h-9 rounded-lg text-center" />
                <Button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg h-9">
                  <Check className="w-4 h-4 mr-1" /> Onayla
                </Button>
                <Button data-testid={`reject-${r.id}`} onClick={() => reject(r.id)} size="sm" variant="outline" className="rounded-lg h-9 text-rose-600 border-rose-200">
                  <X className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        ))}
        {requests.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Henüz talep yok.</p>}
      </div>
    </div>
  );
}

function PaymentsTab({ payments, revenue }) {
  const STATUS = {
    paid: { text: "Ödendi", cls: "bg-emerald-100 text-emerald-700" },
    pending: { text: "Bekliyor", cls: "bg-amber-100 text-amber-700" },
    failed: { text: "Başarısız", cls: "bg-rose-100 text-rose-600" },
  };
  const paidCount = payments.filter((p) => p.status === "paid").length;
  return (
    <div className="space-y-6 mt-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <AStat icon={CreditCard} color="bg-emerald-500" value={`${revenue.toLocaleString("tr-TR")} ₺`} label="Toplam Gelir" />
        <AStat icon={Check} color="bg-indigo-500" value={paidCount} label="Ödenen Abonelik" />
        <AStat icon={Clock} color="bg-amber-500" value={payments.length - paidCount} label="Bekleyen/İptal" />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6">
        <h3 className="font-bold text-slate-900 mb-3">Shopier Ödemeleri ({payments.length})</h3>
        <div className="space-y-2 max-h-[520px] overflow-y-auto" data-testid="admin-payments-list">
          {payments.map((o) => {
            const s = STATUS[o.status] || STATUS.pending;
            return (
              <div key={o.order_id} data-testid={`admin-payment-${o.order_id}`} className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl">
                <div className="flex-1 min-w-[160px]">
                  <p className="text-sm font-semibold text-slate-800">{o.name}</p>
                  <p className="text-xs text-slate-500">{o.email} · {new Date(o.created_at).toLocaleString("tr-TR")}</p>
                </div>
                <span className="font-extrabold text-slate-900">{parseFloat(o.amount).toLocaleString("tr-TR")} ₺</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${s.cls}`}>{s.text}</span>
              </div>
            );
          })}
          {payments.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Henüz ödeme yok.</p>}
        </div>
      </div>
    </div>
  );
}

function AnalyticsTab({ analytics }) {
  if (!analytics) return <p className="text-sm text-slate-400 text-center py-10">İstatistikler yükleniyor...</p>;
  const maxDaily = Math.max(1, ...analytics.daily.map((d) => d.count));
  const Bar = ({ label, count, max, color }) => (
    <div className="flex items-center gap-3">
      <span className="w-40 truncate text-sm text-slate-600" title={label}>{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
        <div className={`${color} h-full rounded-full`} style={{ width: `${(count / max) * 100}%` }} />
      </div>
      <span className="w-10 text-right text-sm font-bold text-slate-700">{count}</span>
    </div>
  );
  const maxPage = Math.max(1, ...analytics.top_pages.map((p) => p.count));
  const maxSrc = Math.max(1, ...analytics.top_sources.map((p) => p.count));
  return (
    <div className="space-y-6 mt-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <AStat icon={Eye} color="bg-indigo-500" value={analytics.total_views} label="Toplam Ziyaret" />
        <AStat icon={Users} color="bg-emerald-500" value={analytics.unique_visitors} label="Tekil Ziyaretçi" />
        <AStat icon={Globe} color="bg-sky-500" value={analytics.top_sources.length} label="Trafik Kaynağı" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-indigo-600" /> Son 7 Gün</h3>
        <div className="flex items-end gap-2 h-40" data-testid="analytics-daily">
          {analytics.daily.length === 0 && <p className="text-sm text-slate-400">Henüz veri yok.</p>}
          {analytics.daily.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full bg-indigo-500 rounded-t-lg transition-all" style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: "4px" }} title={`${d.count} ziyaret`} />
              <span className="text-[10px] text-slate-400">{d.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-bold text-slate-900 mb-4">En Çok Ziyaret Edilen Sayfalar</h3>
          <div className="space-y-3" data-testid="analytics-pages">
            {analytics.top_pages.map((p) => <Bar key={p.label} label={p.label} count={p.count} max={maxPage} color="bg-indigo-500" />)}
            {analytics.top_pages.length === 0 && <p className="text-sm text-slate-400">Henüz veri yok.</p>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-bold text-slate-900 mb-4">Nereden Geliyorlar? (Kaynak)</h3>
          <div className="space-y-3" data-testid="analytics-sources">
            {analytics.top_sources.map((p) => <Bar key={p.label} label={p.label} count={p.count} max={maxSrc} color="bg-emerald-500" />)}
            {analytics.top_sources.length === 0 && <p className="text-sm text-slate-400">Henüz veri yok.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PdfUpload({ endpoint, extraFields, subject, title, label, testid, reload, onDone }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (title !== undefined && !title) { toast.error("Önce konu başlığını yaz"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("subject", subject);
    Object.entries(extraFields || {}).forEach(([k, v]) => fd.append(k, v));
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = data.created ? data.created.length : 1;
      toast.success(`PDF işlendi! ${n} kayıt eklendi 📄`);
      reload && reload();
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF işlenemedi");
    } finally { setBusy(false); }
  };
  return (
    <label data-testid={testid} className="inline-flex items-center gap-2 cursor-pointer bg-slate-800 hover:bg-slate-900 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition">
      <Upload className="w-4 h-4" /> {busy ? "İşleniyor..." : label}
      <input type="file" accept="application/pdf" className="hidden" onChange={onFile} disabled={busy} />
    </label>
  );
}

function AnnouncementsTab({ announcements, reload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const add = async () => {
    if (!text.trim()) { toast.error("Duyuru metni gerekli"); return; }
    setBusy(true);
    try {
      await api.post("/admin/announcements", { text, active: true });
      toast.success("Duyuru eklendi 📢");
      setText("");
      reload();
    } catch { toast.error("Eklenemedi"); } finally { setBusy(false); }
  };

  const toggle = async (a) => {
    await api.put(`/admin/announcements/${a.id}`, { text: a.text, active: !a.active });
    reload();
  };

  const saveEdit = async (a) => {
    if (!editText.trim()) { toast.error("Metin boş olamaz"); return; }
    await api.put(`/admin/announcements/${a.id}`, { text: editText, active: a.active });
    toast.success("Güncellendi");
    setEditId(null);
    reload();
  };

  const del = async (id) => { await api.delete(`/admin/announcements/${id}`); reload(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Megaphone className="w-5 h-5 text-indigo-600" /> Yeni Duyuru</h3>
        <p className="text-sm text-slate-500">Deneme Sınavı sayfasında kayan yazı olarak görünür.</p>
        <Textarea data-testid="announcement-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
          placeholder="Örn: Yeni KKTC Kolej denemesi eklendi! Hemen çöz 🎯" className="rounded-xl" />
        <Button data-testid="add-announcement-btn" onClick={add} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-xl font-bold">
          <Plus className="w-4 h-4 mr-1" /> Duyuru Ekle
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-bold text-slate-900 mb-3">Duyurular ({announcements.length})</h3>
        <div className="space-y-2 max-h-[520px] overflow-y-auto">
          {announcements.map((a) => (
            <div key={a.id} data-testid={`announcement-${a.id}`} className="p-3 bg-slate-50 rounded-xl">
              {editId === a.id ? (
                <div className="space-y-2">
                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="rounded-lg text-sm" data-testid={`announcement-edit-input-${a.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEdit(a)} data-testid={`announcement-save-${a.id}`} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg h-8">Kaydet</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)} className="rounded-lg h-8">İptal</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className={`flex-1 text-sm ${a.active ? "text-slate-700" : "text-slate-400 line-through"}`}>{a.text}</span>
                  <button onClick={() => toggle(a)} data-testid={`announcement-toggle-${a.id}`}
                    className={`text-xs font-bold px-2 py-1 rounded-full ${a.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                    {a.active ? "Aktif" : "Pasif"}
                  </button>
                  <button onClick={() => { setEditId(a.id); setEditText(a.text); }} data-testid={`announcement-edit-${a.id}`} className="text-slate-400 hover:text-indigo-600"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(a.id)} data-testid={`announcement-del-${a.id}`} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          ))}
          {announcements.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Henüz duyuru yok.</p>}
        </div>
      </div>
    </div>
  );
}


function TopicsTab({ topics, reload }) {
  const empty = { subject: "Matematik", title: "", description: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!f.title) { toast.error("Konu başlığı gerekli"); return; }
    setBusy(true);
    try {
      await api.post("/admin/topics", f);
      toast.success("Ders konusu eklendi 📚");
      setF(empty);
      reload();
    } catch { toast.error("Eklenemedi"); } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/admin/topics/${id}`); reload(); };

  const bySubject = SUBJECTS.map((s) => ({ subject: s, items: topics.filter((t) => t.subject === s) }));

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-bold text-slate-900">Yeni Ders Konusu Ekle</h3>
        <p className="text-sm text-slate-500">Öğrenciler bu konuyu seçtiğinde Nokta Öğretmen anlatır.</p>
        <div>
          <Label className="text-sm font-semibold">Ders</Label>
          <Select value={f.subject} onValueChange={(v) => setF({ ...f, subject: v })}>
            <SelectTrigger data-testid="topic-subject" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-semibold">Konu Başlığı</Label>
          <Input data-testid="topic-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Örn: Çarpanlar ve Katlar" className="mt-1 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Açıklama (AI'ya yol gösterir)</Label>
          <Textarea data-testid="topic-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 rounded-xl" rows={3} placeholder="Bu konuda hangi kazanımlar anlatılmalı?" />
        </div>
        <Button data-testid="add-topic-btn" onClick={add} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 rounded-xl font-bold"><Plus className="w-4 h-4 mr-1" /> Konu Ekle</Button>
        <div className="pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-2">Ya da PDF yükle — içeriği Nokta Öğretmen konu anlatımında kaynak alır. (Başlık + ders gerekli)</p>
          <PdfUpload endpoint="/admin/upload-pdf-topic" subject={f.subject} title={f.title}
            extraFields={{ title: f.title, description: f.description }}
            label="PDF'den Konu Yükle" testid="upload-topic-pdf" reload={reload}
            onDone={() => setF(empty)} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-bold text-slate-900 mb-3">Ders Konuları ({topics.length})</h3>
        <div className="space-y-4 max-h-[520px] overflow-y-auto">
          {bySubject.map((grp) => grp.items.length > 0 && (
            <div key={grp.subject}>
              <p className="text-xs font-bold text-slate-400 uppercase mb-1">{grp.subject}</p>
              <div className="space-y-1">
                {grp.items.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-xl" data-testid={`admin-topic-${t.id}`}>
                    <span className="flex-1 text-sm text-slate-700">{t.title}</span>
                    <button onClick={() => del(t.id)} data-testid={`del-topic-${t.id}`} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {topics.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Henüz konu yok.</p>}
        </div>
      </div>
    </div>
  );
}

function QuestionsTab({ questions, reload }) {
  const empty = { subject: "Matematik", text: "", options: ["", "", "", ""], correct_index: 0, explanation: "", source: "real", image_url: null };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  const uploadImg = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImgBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Görsel eklendi 🖼️");
    } catch { toast.error("Görsel yüklenemedi"); } finally { setImgBusy(false); }
  };

  const add = async () => {
    if (!f.text || f.options.some((o) => !o)) { toast.error("Tüm alanları doldur"); return; }
    setBusy(true);
    try {
      await api.post("/admin/questions", f);
      toast.success("Soru eklendi ✅");
      setF(empty);
      reload();
    } catch { toast.error("Eklenemedi"); } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/admin/questions/${id}`); reload(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-bold text-slate-900">Gerçek Soru Ekle</h3>
        <div>
          <Label className="text-sm font-semibold">Ders</Label>
          <Select value={f.subject} onValueChange={(v) => setF({ ...f, subject: v })}>
            <SelectTrigger data-testid="q-subject" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-semibold">Soru Metni</Label>
          <Textarea data-testid="q-text" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className="mt-1 rounded-xl" rows={2} />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Seçenekler (doğru olanı işaretle)</Label>
          {f.options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" name="correct" checked={f.correct_index === i} onChange={() => setF({ ...f, correct_index: i })} data-testid={`q-correct-${i}`} className="w-4 h-4 accent-indigo-600" />
              <Input data-testid={`q-option-${i}`} value={opt} onChange={(e) => { const o = [...f.options]; o[i] = e.target.value; setF({ ...f, options: o }); }} placeholder={`${String.fromCharCode(65 + i)} seçeneği`} className="rounded-xl" />
            </div>
          ))}
        </div>
        <div>
          <Label className="text-sm font-semibold">Açıklama (yanlış yapan öğrenciye gösterilir)</Label>
          <Textarea data-testid="q-explanation" value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.target.value })} className="mt-1 rounded-xl" rows={2} />
        </div>
        <div>
          <Label className="text-sm font-semibold">Görsel (geometri şekli / tablo / grafik) — isteğe bağlı</Label>
          <div className="mt-1 flex items-center gap-3">
            <label data-testid="q-image-upload" className="inline-flex items-center gap-2 cursor-pointer bg-slate-800 hover:bg-slate-900 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition">
              <ImageIcon className="w-4 h-4" /> {imgBusy ? "Yükleniyor..." : "Görsel Yükle"}
              <input type="file" accept="image/*" className="hidden" onChange={uploadImg} disabled={imgBusy} />
            </label>
            {f.image_url && (
              <div className="flex items-center gap-2">
                <img src={`${BE}${f.image_url}`} alt="önizleme" className="w-14 h-14 object-cover rounded-lg border" />
                <button type="button" onClick={() => setF({ ...f, image_url: null })} className="text-slate-400 hover:text-rose-500"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
        <Button data-testid="add-question-btn" onClick={add} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-xl font-bold"><Plus className="w-4 h-4 mr-1" /> Soru Ekle</Button>
        <div className="pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-2">Ya da PDF yükle — yapay zeka sorulari çıkarıp bankaya ekler.</p>
          <PdfUpload endpoint="/admin/upload-pdf-questions" subject={f.subject}
            label="PDF'den Soru Yükle" testid="upload-question-pdf" reload={reload} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-bold text-slate-900 mb-3">Soru Bankası ({questions.length})</h3>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {questions.map((q) => (
            <div key={q.id} className="flex items-start gap-2 p-3 bg-slate-50 rounded-xl" data-testid={`admin-question-${q.id}`}>
              <div className="flex-1">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${q.source === "ai" ? "bg-purple-100 text-purple-600" : q.source === "pdf" ? "bg-orange-100 text-orange-600" : "bg-blue-100 text-blue-600"}`}>{q.subject} · {q.source === "ai" ? "AI" : q.source === "pdf" ? "PDF" : "Gerçek"}</span>
                {q.image_url && <span className="ml-1 text-xs">🖼️</span>}
                <p className="text-sm text-slate-700 mt-1"><QuestionText text={q.text} /></p>
                {q.image_url && <img src={`${BE}${q.image_url}`} alt="şekil" className="mt-2 max-h-40 rounded-lg border border-slate-200" />}
              </div>
              <button onClick={() => del(q.id)} data-testid={`del-question-${q.id}`} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {questions.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Henüz soru yok.</p>}
        </div>
      </div>
    </div>
  );
}

function GenerateTab({ reload }) {
  const [f, setF] = useState({ subject: "Matematik", topic: "", count: 5 });
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    if (!f.topic) { toast.error("Konu yaz"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/generate-questions", f);
      toast.success(`${data.created.length} soru üretildi! ✨`);
      reload();
    } catch { toast.error("Üretilemedi, tekrar dene"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg bg-white border border-slate-200 rounded-2xl p-6 space-y-4 mt-4">
      <h3 className="font-bold text-slate-900">✨ Yapay Zeka ile Soru Üret</h3>
      <p className="text-sm text-slate-500">Nokta Öğretmen KKTC Kolej formatında benzer sorular üretir.</p>
      <div>
        <Label className="text-sm font-semibold">Ders</Label>
        <Select value={f.subject} onValueChange={(v) => setF({ ...f, subject: v })}>
          <SelectTrigger data-testid="gen-subject" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-sm font-semibold">Konu</Label>
        <Input data-testid="gen-topic" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} placeholder="Örn: Kesirler" className="mt-1 rounded-xl" />
      </div>
      <div>
        <Label className="text-sm font-semibold">Soru Sayısı</Label>
        <Input data-testid="gen-count" type="number" min={1} max={10} value={f.count} onChange={(e) => setF({ ...f, count: parseInt(e.target.value) || 5 })} className="mt-1 rounded-xl" />
      </div>
      <Button data-testid="generate-btn" onClick={gen} disabled={busy} className="w-full bg-purple-600 hover:bg-purple-700 rounded-xl font-bold py-6">
        {busy ? "Üretiliyor... 🤖" : <><Sparkles className="w-4 h-4 mr-1" /> Soruları Üret</>}
      </Button>
    </div>
  );
}

function ExamsTab({ exams, questions, reload }) {
  const [f, setF] = useState({ title: "", description: "", duration_min: 40, question_ids: [] });
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    setF((p) => ({ ...p, question_ids: p.question_ids.includes(id) ? p.question_ids.filter((x) => x !== id) : [...p.question_ids, id] }));
  };

  const create = async () => {
    if (!f.title || f.question_ids.length === 0) { toast.error("Başlık ve en az 1 soru gerekli"); return; }
    setBusy(true);
    try {
      await api.post("/admin/exams", f);
      toast.success("Deneme sınavı oluşturuldu 🎓");
      setF({ title: "", description: "", duration_min: 40, question_ids: [] });
      reload();
    } catch { toast.error("Oluşturulamadı"); } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/admin/exams/${id}`); reload(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-bold text-slate-900">Yeni Deneme Sınavı</h3>
        <div><Label className="text-sm font-semibold">Başlık</Label>
          <Input data-testid="exam-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Örn: 1. Deneme Sınavı" className="mt-1 rounded-xl" /></div>
        <div><Label className="text-sm font-semibold">Açıklama</Label>
          <Input data-testid="exam-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 rounded-xl" /></div>
        <div><Label className="text-sm font-semibold">Süre (dakika)</Label>
          <Input data-testid="exam-duration" type="number" value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: parseInt(e.target.value) || 40 })} className="mt-1 rounded-xl" /></div>
        <div>
          <Label className="text-sm font-semibold">Sorular ({f.question_ids.length} seçildi)</Label>
          <div className="mt-1 max-h-64 overflow-y-auto space-y-1 border border-slate-200 rounded-xl p-2">
            {questions.map((q) => (
              <label key={q.id} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded-lg cursor-pointer text-sm">
                <input type="checkbox" checked={f.question_ids.includes(q.id)} onChange={() => toggle(q.id)} data-testid={`select-q-${q.id}`} className="mt-1 accent-indigo-600" />
                <span><span className="text-xs font-bold text-indigo-500">{q.subject}</span> · {q.text.slice(0, 60)}</span>
              </label>
            ))}
            {questions.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Önce soru ekle.</p>}
          </div>
        </div>
        <Button data-testid="create-exam-btn" onClick={create} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl font-bold"><Plus className="w-4 h-4 mr-1" /> Deneme Oluştur</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-bold text-slate-900 mb-3">Mevcut Denemeler ({exams.length})</h3>
        <div className="space-y-2">
          {exams.map((e) => (
            <div key={e.id} className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl" data-testid={`admin-exam-${e.id}`}>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 text-sm">{e.title}</p>
                <p className="text-xs text-slate-400">{e.question_count} soru · {e.duration_min} dk</p>
              </div>
              <button onClick={() => del(e.id)} data-testid={`del-exam-${e.id}`} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {exams.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Henüz deneme yok.</p>}
        </div>
      </div>
    </div>
  );
}
